import { Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { SelectItemDto } from "../../../../types/SelectItemDto";
import { ClaimProfesionalDto } from "../../types";
import { ClaimProfessionalRoleEnum } from "../../types/enums";

interface ClaimProfessionalFormProps {
    professional: ClaimProfesionalDto;
    readOnly?: boolean;
    canRemove?: boolean;
    serviceIndicators?: SelectItemDto[];
    claimProfessionalTypes?: SelectItemDto[];
    nameError?: string | null;
    onChange: (
        field: keyof ClaimProfesionalDto,
        value: ClaimProfesionalDto[keyof ClaimProfesionalDto]
    ) => void;

    onRemove?: () => void;
    onSave?: () => void;
    onCancel?: () => void;
}

export default function ClaimProfessionalForm({
    professional,
    readOnly = false,
    canRemove =false,
    serviceIndicators,
    claimProfessionalTypes,
    nameError = null,
    onChange,
    onRemove,
    onSave,
    onCancel
}: ClaimProfessionalFormProps) {
    const { t } = useTranslation("claim");
    const isExistingProfessional = professional.professionalId > 0;

    const isNewProfessional = !isExistingProfessional;

    return (
        <Row className="claim-professional-container" >
            <Col style={{ width: "95%" }}>
                {!isExistingProfessional && (
                    <div className="card-tag manual">
                        {t("claim:newProfessionalManuallyAdded")}
                    </div>
                )}
                <Row>
                    {/* Nombre */}
                    <Col xs={6} md={6}>
                        <Form.Group>
                            <Form.Label>
                                {t("claim:newClaimInputProfessionalName")}
                            </Form.Label>

                            <Form.Control
                                type="text"
                                value={professional.nameAndSurname ?? ""}
                                readOnly={readOnly || isExistingProfessional}
                                isInvalid={!!nameError} 
                                onChange={(e) =>
                                    onChange("nameAndSurname",e.target.value)
                                }
                            />
                            <Form.Control.Feedback type="invalid">
                                {nameError}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>

                    {/* Email */}
                    <Col xs={6} md={6}>
                        <Form.Group>
                            <Form.Label>
                                {t("claim:newClaimInputProfessionalEmail")}
                            </Form.Label>

                            <Form.Control
                                type="text"
                                value={professional.email ?? ""}
                                readOnly={readOnly || isExistingProfessional}
                                onChange={(e) =>
                                    onChange( "email",e.target.value )
                                }
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    {/* Servicio */}
                    <Col xs={3} md={3}>
                        <Form.Group>
                            <Form.Label>
                                {t("claim:newClaimInputProfessionalService")}
                            </Form.Label>

                            <Form.Select
                                value={ professional.serviceIndicatorId ?? ""}
                                disabled={readOnly || isExistingProfessional}
                                onChange={(e) =>
                                    onChange(
                                        "serviceIndicatorId",
                                        e.target.value
                                            ? parseInt(e.target.value)
                                            : null
                                    )
                                }
                            >
                                <option value=""></option>

                                {serviceIndicators?.map(
                                    (serviceIndicator) => (
                                        <option
                                            key={serviceIndicator.id}
                                            value={serviceIndicator.id}
                                        >
                                            {serviceIndicator.name}
                                        </option>
                                    )
                                )}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    {/* Categor�a */}
                    <Col xs={3} md={3}>
                        <Form.Group>
                            <Form.Label>
                                {t(
                                    "claim:newClaimInputProfessionalCategory"
                                )}
                            </Form.Label>

                            <Form.Select
                                value={professional.typeProfessionalId ?? "" }
                                disabled={readOnly || isExistingProfessional}
                                onChange={(e) => onChange("typeProfessionalId", e.target.value ?
                                    parseInt(e.target.value) : null
                                )}
                            >
                                <option value=""></option>

                                {claimProfessionalTypes?.map((type) => (
                                    <option
                                        key={type.id}
                                        value={type.id}
                                    >
                                        {type.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    {/* N� colegiado */}
                    <Col xs={3} md={3}>
                        <Form.Group>
                            <Form.Label>
                                {t("claim:newClaimInputProfessionalRegisteredNumber")}
                            </Form.Label>

                            <Form.Control
                                type="text"
                                value={professional.registeredNumber ?? ""}
                                readOnly={readOnly || isExistingProfessional }
                                onChange={(e) =>
                                    onChange("registeredNumber",e.target.value)
                                }
                            />
                        </Form.Group>
                    </Col>

                    {/* Rol */}
                    <Col xs={3} md={3}>
                        <Form.Group>
                            <Form.Label>
                                {t(
                                    "claim:newClaimInputProfessionalProfessionalRole"
                                )}
                            </Form.Label>

                            <Form.Select
                                value={ professional.claimProfessionalRole ?? ""}
                                disabled={  readOnly }
                                onChange={(e) =>
                                    onChange(
                                        "claimProfessionalRole",
                                        e.target.value
                                            ? parseInt(e.target.value)
                                            : null
                                    )
                                }
                            >
                                <option value=""></option>

                                {Object.entries(
                                    ClaimProfessionalRoleEnum
                                )
                                    .filter(
                                        ([key]) =>
                                            isNaN(Number(key))
                                    )
                                    .map(([key, value]) => (
                                        <option
                                            key={value}
                                            value={value}
                                        >
                                            {t(`claim:newClaimInputProfessionalProfessionalRole${key}`)}
                                        </option>
                                    ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                {/* Botones del formulario nuevo */}
                {isNewProfessional && !readOnly && (
                    <Row className="mt-3">
                        <Col className="text-end">
                            {onCancel && (
                                <Button
                                    variant="secondary"
                                    className="me-2"
                                    onClick={onCancel}
                                >
                                    {t("common:cancel")}
                                </Button>
                            )}

                            {onSave && (
                                <Button
                                    variant="primary"
                                    onClick={onSave}
                                >
                                    {t("common:save")}
                                </Button>
                            )}
                        </Col>
                    </Row>
                )}
            </Col>

            {/* Eliminar profesional existente */}
            {canRemove && onRemove && !readOnly && (
                <Col
                    md={1}
                    style={{ width: "5%" }}
                    className="d-flex justify-content-center align-items-center"
                >
                    <Button
                        variant="secondary"
                        onClick={onRemove}
                    >
                        <i className="bi bi-x-lg"></i>
                    </Button>
                </Col>
            )}
        </Row>
    );
}

